﻿<script setup lang="ts">
import SettingsJsonEditor from "@/components/settings/SettingsJsonEditor.vue";
import {ref} from 'vue';
import {Icon} from '@iconify/vue';
import {useSettingsStore} from "@/stores/settings.ts";
import {storeToRefs} from "pinia";
import Collapsible from "@/components/generic/Collapsible.vue";


const expanded = defineModel<boolean>('expanded', {default: true});

const jsonEditorVisible = ref(false);

const settingsStore = useSettingsStore();
const {mode, settings} = storeToRefs(settingsStore);

const field = settingsStore.field;
const lyricField = settingsStore.lyricField;

// Druck/Zug-Ausgleich (nur Auto-Modus): Enable-Schalter + Zahlenfeld-Model kommen aus dem
// Settings-Store (beamBalanceModel), damit beamMaxDruckZugRatio.spec.ts den echten Code testet.
const {balanceEnabled, maxRatioModel} = settingsStore.beamBalanceModel();

// Alternative position settings

const maxAlternativesDruck = field('MAX_ALTERNATIVES_DRUCK');
const maxAlternativesZug = field('MAX_ALTERNATIVES_ZUG');


// Core conversion settings

const enforceCMajor = field('ENFORCE_C_MAJOR');
const correctTakt = field('FixTakt');
const addMeasurements = field('AddMeasurements');
const append = field('Append');

// Beam search settings

// Debug settings
const debugOutput = field('DEBUG_OUTPUT');

// Lyric settings
const lyricPreserve = lyricField('Preserve');
const lyricDebugScore = lyricField('DebugScore');
const lyricDebugScoreAuto = lyricField('DebugScoreAuto');
const lyricNote = lyricField('Note');
const lyricBass = lyricField('Bass');
// Der frühere `lyricMeasureKey`-Schalter (Lyrics.MeasureKey) ist entfallen: das Feld wurde von
// der Engine NIE gelesen – der Haken hat schlicht nichts bewirkt.
const lyricBassKey = lyricField('BassKey');
const lyricMeasureScore = lyricField('MeasureScore');
const lyricAddTotalScore = lyricField('AddTotalScore');

// instrument currently local UI state; wire to settings later if needed

function toggleEditor() {
  jsonEditorVisible.value = !jsonEditorVisible.value;
}
</script>

<template>
  <Collapsible variant='card' icon="fa6-solid:sliders" title="Einstellungen" v-model:is-open="expanded">


  <template #default>
  <!-- Alternative Positions -->

  <div v-show="'global'==mode" class="mb-8 p-5 bg-gray-50/50 rounded-2xl border border-gray-100">
    <label class="block text-sm font-semibold text-gray-700 mb-4 uppercase tracking-wide flex items-center gap-2">
      <Icon icon="fa6-solid:shuffle" class="text-gray-400"/>
      Alternative Griffpositionen
    </label>

    <div class="space-y-6 max-w-screen">
      <!-- Druck -->
      <div>
        <div class="flex justify-between mb-2">
          <span class="text-sm font-medium text-gray-700">Druck-Alternativen</span>
          <span class="text-sm font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded">{{ maxAlternativesDruck }}</span>
        </div>
        <input
            type="range"
            min="0"
            max="10"
            v-model.number="maxAlternativesDruck"
            class="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
        />
      </div>

      <!-- Zug -->
      <div>
        <div class="flex justify-between mb-2">
          <span class="text-sm font-medium text-gray-700">Zug-Alternativen</span>
          <span class="text-sm font-bold text-purple-600 bg-purple-50 px-2 py-0.5 rounded">{{ maxAlternativesZug }}</span>
        </div>
        <input
            type="range"
            min="0"
            max="10"
            v-model.number="maxAlternativesZug"
            class="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
        />
      </div>
    </div>
  </div>

  <div class="group border border-gray-200 rounded-xl overflow-hidden bg-white transition-all duration-300 open:shadow-md">

  <div class="p-5 space-y-8 bg-white">
  <!-- Core Conversion Settings -->
  <div>
    <h3 class="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-2">
      <Icon icon="game-icons:f-clef"/>
      Konvertierung
    </h3>
    <div class="flex flex-wrap gap-1">

      <label class="items-center p-1 rounded-lg border border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors">
        <input type="checkbox" class="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500" v-model="enforceCMajor"/>
        <span class="ml-3 text-sm font-medium text-gray-700">C-Dur erzwingen</span>
      </label>
      <label class="items-center p-1 rounded-lg border border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors">
        <input type="checkbox" class="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500" v-model="append"/>
        <span class="ml-3 text-sm font-medium text-gray-700">An bestehendes Score anhängen</span>
      </label>
      <label class="items-center p-1 rounded-lg border border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors">
        <input type="checkbox" class="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500" v-model="addMeasurements"/>
        <span class="ml-3 text-sm font-medium text-gray-700">Fehlende Takte hinzufügen</span>
      </label>
      <label class="items-center p-1 rounded-lg border border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors">
        <input type="checkbox" class="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500" v-model="correctTakt"/>
        <span class="ml-3 text-sm font-medium text-gray-700">Taktlängen korrigieren</span>
      </label>

    </div>
  </div>


  <!-- Beam Search Settings -->
  <div class="pt-2 border-t border-gray-100">
    <h3 class="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-2">
      <Icon icon="fa6-solid:magnifying-glass"/>
      Optimierungsalgorithmus
    </h3>
    <div class="flex flex-wrap " v-if="settings.Beam">
      <label v-if="mode==='auto'" class="items-center p-1 rounded-lg border border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors">
        <input type="checkbox" class="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500" v-model="balanceEnabled"/>
        <span class="ml-3 text-sm font-medium text-gray-700">Druck/Zug Ausgleich</span>
      </label>
      <label class="items-center p-1 rounded-lg border border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors">
        <input type="checkbox" class="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500" v-model="settings.Beam.use_beam_search"/>
        <span class="ml-3 text-sm font-medium text-gray-700">Beam Search verwenden</span>
      </label>

      <label class="items-center p-1 rounded-lg border border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors">
        <input type="checkbox" class="w-4 h-4 text-gray-400 rounded border-gray-300 focus:ring-gray-500" v-model="settings.Beam.enable_dubug"/>
        <span class="ml-3 text-sm font-medium text-gray-500">Beam Search Debug-Ausgabe</span>
      </label>
    </div>
    <!-- Max. Druck/Zug-Verhältnis: nur im Auto-Modus und wenn der Ausgleich aktiv ist. -->
    <div v-if="mode==='auto' && settings.Beam && balanceEnabled" class="mt-2 flex items-center gap-2">
      <label class="text-sm font-medium text-gray-700" for="maxDruckZugRatio">Max. Druck/Zug-Verhältnis</label>
      <input
          id="maxDruckZugRatio"
          type="number"
          min="1"
          step="0.1"
          class="w-24 px-2 py-1 text-sm rounded-lg border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
          v-model.number="maxRatioModel"
      />
      <span class="text-xs text-gray-500">z. B. 2.2 = höchstens 11:5</span>
    </div>
  </div>

  <!-- Lyric Settings -->
  <div class=" pt-4 border-t border-gray-100">
    <h3 class="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1">
      <Icon icon="fa6-solid:align-left"/>
      Liedtext & Beschriftungen
    </h3>
    <div class="flex flex-wrap gap-1">
      <label class="items-center p-1 rounded-lg border border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors">
        <input type="checkbox" class="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500" v-model="lyricPreserve"/>
        <span class="ml-3 text-sm font-medium text-gray-700">Liedtexte beibehalten</span>
      </label>
      <label class="items-center p-1 rounded-lg border border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors">
        <input type="checkbox" class="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500" v-model="lyricNote"/>
        <span class="ml-3 text-sm font-medium text-gray-700">Notenname anzeigen</span>
      </label>
      <label class="items-center p-1 rounded-lg border border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors">
        <input type="checkbox" class="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500" v-model="lyricBass"/>
        <span class="ml-3 text-sm font-medium text-gray-700">Bass-Angaben anzeigen</span>
      </label>
      <label class="items-center p-1 rounded-lg border border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors">
        <input type="checkbox" class="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500" v-model="lyricBassKey"/>
        <span class="ml-3 text-sm font-medium text-gray-700">Bass-Tonart anzeigen</span>
      </label>
      <label class="items-center p-1 rounded-lg border border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors">
        <input type="checkbox" class="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500" v-model="lyricMeasureScore"/>
        <span class="ml-3 text-sm font-medium text-gray-700">Takt-Score anzeigen</span>
      </label>
      <label class="items-center p-1 rounded-lg border border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors">
        <input type="checkbox" class="w-4 h-4 text-gray-400 rounded border-gray-300 focus:ring-gray-500" v-model="lyricDebugScore"/>
        <span class="ml-3 text-sm font-medium text-gray-500">Debug-Score anzeigen</span>
      </label>
      <label class="items-center p-1 rounded-lg border border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors">
        <input type="checkbox" class="w-4 h-4 text-gray-400 rounded border-gray-300 focus:ring-gray-500" v-model="lyricDebugScoreAuto"/>
        <span class="ml-3 text-sm font-medium text-gray-500">Auto Debug-Score</span>
      </label>
      <label class="items-center p-1 rounded-lg border border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors">
        <input type="checkbox" class="w-4 h-4 text-gray-400 rounded border-gray-300 focus:ring-gray-500" v-model="lyricAddTotalScore"/>
        <span class="ml-3 text-sm font-medium text-gray-500">Gesamt-Score im Titel</span>
      </label>
    </div>
  </div>

  <!-- Debug Settings -->
  <div class="space-y-3 pt-4 border-t border-gray-100">
    <h3 class="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-2">
      <Icon icon="fa6-solid:bug"/>
      Debug-Optionen
    </h3>
    <label class="items-center p-1 rounded-lg border border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors">
      <input type="checkbox" class="w-4 h-4 text-gray-400 rounded border-gray-300 focus:ring-gray-500" v-model="debugOutput"/>
      <span class="ml-3 text-sm font-medium text-gray-500">Debug-Ausgabe aktivieren</span>
    </label>
  </div>

  <div class="pt-4 mt-4 border-t border-gray-100">
    <button
        id="toggleJsonEditor"
        class="w-full btn btn-secondary flex items-center justify-center gap-2"
        @click="toggleEditor"
    >
      <Icon icon="fa6-solid:code"/>
      <span>JSON-Einstellungen bearbeiten</span>
    </button>
  </div>
  </div>
  </div>

  <SettingsJsonEditor v-if="jsonEditorVisible"/>
  </template>
  </Collapsible>

</template>
